<template>
  <div class="ride-copied-users mt-5" :dir="isHebrew ? 'rtl' : 'ltr'">
    <h5 class="mb-3" :style="{ textAlign: isHebrew ? 'right' : 'left' }">{{ t('rideCopiedUsersTitle') }}</h5>

    <b-button v-if="!loaded" variant="outline-primary" size="sm" @click="fetchUsers" :disabled="loading">
      <span v-if="loading" class="spinner-border spinner-border-sm mr-1" role="status" />
      {{ t('rideCopiedUsersLoadBtn') }}
    </b-button>

    <div v-else-if="users.length === 0" class="text-muted pb-5" :class="isHebrew ? 'text-right' : 'text-left'">
      {{ t('rideCopiedUsersEmpty') }}
    </div>

    <div v-else>
      <div
        v-for="user in users"
        :key="user.ride_id"
        class="user-row"
      >
        <div class="user-info" :class="isHebrew ? 'text-right' : 'text-left'">
          <div class="user-email">{{ user.email }}</div>
          <div class="user-meta">
            <span class="user-ride-id">#{{ user.ride_id }}</span>
            <span class="user-auth"> · {{ user.auth_provider === 'google' ? t('rideCopiedUsersAuthGoogle') : t('rideCopiedUsersAuthEmail') }}</span>
            <span class="user-date"> · {{ formatDate(user.copied_at) }}</span>
          </div>
        </div>

        <div class="user-actions">
          <span v-if="user.successMessage" class="text-success small action-msg">{{ user.successMessage }}</span>
          <b-button
            v-if="user.auth_provider === 'email'"
            size="sm"
            variant="outline-secondary"
            class="action-btn"
            :title="t('rideCopiedUsersResetPasswordTooltip')"
            @click="resetPassword(user)"
          >
            {{ t('rideCopiedUsersResetPasswordBtn') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';
import { mapGetters, mapActions } from 'vuex';
import manageRidePrivacyApi from '@/api/manageRidePrivacy';
import appauthApi from '@/api/appauth';

export default {
  name: 'RideCopiedUsers',
  mixins: [translationMixin],
  props: {
    rideId: { type: [Number, String], required: true },
  },
  data() {
    return {
      loading: false,
      loaded: false,
      users: [],
    };
  },
  computed: {
    ...mapGetters(['apptoken']),
  },
  methods: {
    ...mapActions(['showModal']),
    async fetchUsers() {
      this.loading = true;
      try {
        const res = await manageRidePrivacyApi.getRideCopiedUsers(this.apptoken, this.rideId);
        this.users = (res.data.users || []).map(u => ({ ...u, successMessage: '' })).reverse();
        this.loaded = true;
      } catch (e) {
        this.users = [];
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },
    async resetPassword(item) {
      try {
        this.showModal('loadingIcon');
        await appauthApi.requestPasswordReset(item.email, window.location.origin);
        const idx = this.users.findIndex(u => u.ride_id === item.ride_id);
        if (idx !== -1) {
          this.$set(this.users[idx], 'successMessage', this.t('rideCopiedUsersResetPasswordSuccess'));
        }
      } catch (e) {
        // silent
      } finally {
        this.showModal('');
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.ride-copied-users {
  border-top: 1px solid #dee2e6;
  padding-top: 1.5rem;
  padding-bottom: 4rem;
}

.user-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 1rem;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-email {
  font-weight: 500;
  word-break: break-all;
}

.user-meta {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 2px;
}

.user-ride-id {
  font-family: monospace;
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  padding-inline-start: 2rem;
}

.action-btn {
  white-space: nowrap;
}

.action-msg {
  white-space: nowrap;
}
</style>
